import axios from 'axios'
import { REGENERATE_TOKEN } from './apiConstant';

const api = axios.create({
  baseURL: process.env.REACT_APP_LOCAL_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Add this for credentials
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let pendingQueue = [];

const resolveQueue = (newToken, err) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (err) reject(err);
    else resolve(newToken);
  });
  pendingQueue = [];
};

const forceLogout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('permissions');
  const currentPath = window.location.pathname;
  if (currentPath !== '/auth/login' && currentPath !== '/auth/signup') {
    window.location.href = '/auth/login';
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!error.response) {
      return Promise.reject(error);
    }

    if (error.response.status === 403) {
      console.error('Access forbidden:', error.response.data);
      return Promise.reject(error);
    }

    if (error.response.status === 401 && originalRequest && !originalRequest._retry) {
      const currentPath = window.location.pathname;
      if (currentPath === '/auth/login' || currentPath === '/auth/signup') {
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        forceLogout();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_LOCAL_BASE_URL}${REGENERATE_TOKEN}`,
          {},
          { headers: { Authorization: `Bearer ${refreshToken}` } },
        );

        let newAccessToken = data?.data?.[0]?.access_token || null;
        let newRefreshToken = data?.data?.[0]?.refresh_token || null;
        if (newAccessToken?.startsWith('Bearer ')) newAccessToken = newAccessToken.substring(7);
        if (newRefreshToken?.startsWith('Bearer ')) newRefreshToken = newRefreshToken.substring(7);

        if (!newAccessToken) {
          throw new Error('Refresh response did not contain a new access token');
        }

        localStorage.setItem('token', newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }

        resolveQueue(newAccessToken, null);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        resolveQueue(null, refreshError);
        console.warn('Session refresh failed — logging out.');
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default api